import React from 'react'
import { Form, Input, Select, Button, Alert } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { questionActions } from '../../../../../store/slices/question.slice'
import ResizableDrawer from '../../../../../components/common/resizable-drawer/ResizableDrawer'
import questionSelectors from '../../../../../store/selectors/question.selectors'
import { useVersionId } from '../../../../../utils/parseURL.helper'
import { useFetchQuestionsQuery, useCreateComplexQuestionMutation } from '../../../../../store/apis/question.api'
import { flatten } from '../../../../../utils/array.helper'

const CreateComplexQuestionDrawer = () => {
    const showCreateComplexQuestionDrawer = useSelector(questionSelectors.showCreateComplexQuestionDrawer)
    const dispatch = useDispatch()
    const { token } = useParams()
    const versionId = useVersionId()
    const [form] = Form.useForm()

    const { data: questions } = useFetchQuestionsQuery({ token, versionId })
    const [createComplexQuestion, { isLoading: isCreating, error }] = useCreateComplexQuestionMutation()

    const availableQuestions = React.useMemo(() => {
        if (!questions) return []
        return flatten(questions, { excludeParents: true }).map(question => ({
            value: question.id,
            label: question.name || question.content,
        }))
    }, [questions])

    const handleClose = () => {
        dispatch(questionActions.setShowCreateComplexQuestionDrawer(false))
        form.resetFields()
    }

    const handleSubmit = async (values) => {
        try {
            await createComplexQuestion({
                token,
                name: values.name,
                children: values.children
            }).unwrap()
            
            dispatch(questionActions.setShowCreateComplexQuestionDrawer(false))
            form.resetFields()
        } catch (error) {
            console.error('Failed to create complex question:', error)
        }
    }

    if (!showCreateComplexQuestionDrawer) return null

    return (
        <ResizableDrawer
            title='Create Complex Question'
            open
            autoFocus
            onClose={handleClose}
            mask={null}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark={false}
            >
                <Form.Item
                    name="name"
                    label="Name"
                    rules={[
                        { required: true, message: 'Please enter a name for the complex question' },
                    ]}
                >
                    <Input placeholder="Enter complex question name" />
                </Form.Item>

                <Form.Item
                    name="children"
                    label="Child Questions"
                    rules={[
                        { required: true, message: 'Please select at least one child question' },
                    ]}
                >
                    <Select
                        mode="multiple"
                        placeholder="Select questions to include"
                        options={availableQuestions}
                        showSearch
                        filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>

                {error && (
                    <Alert
                        message="Error creating complex question"
                        description={error?.data?.message || 'An error occurred'}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form.Item>
                    <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={isCreating}
                        block
                    >
                        Create
                    </Button>
                </Form.Item>
            </Form>
        </ResizableDrawer>
    )
}

export default CreateComplexQuestionDrawer 